// TODO(dev:dashboard_v2, translations): Add missing translations
import React, { useState, useMemo } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from "~/react/ui/sheet"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { ButtonGroup } from "../ui/button-group"
// @ts-ignore - CurrencyHelper is a global helper
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useCompanySearch } from "../hooks/useCompanySearch"
import { useCompanySave } from "../hooks/useCompanySave"
import { useCompanyLists } from "./company-lists-context"
import { CompanyFilters, filterCompanies } from "./search/company-filters"
import { CompaniesList } from "./search/companies-list"
import { CompanySaveManager } from "./company-save-manager"
import { CompanyFiltersType, Location, Company } from "~/types/company"

const clearFilters = (setFilters: (filters: CompanyFiltersType) => void) => {
  setFilters({
    location: [],
    industries: [],
    companySizes: [],
    companyTypes: [],
    technologies: [],
  })
}

function OpportunityCompaniesSidepanel({
  id,
  domainIds,
  isOpen,
  onOpenChange,
}: {
  id: string
  domainIds: number[]
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}): React.ReactNode {
  const { companyLists, setCompanyLists } = useCompanyLists()

  const { companies, isLoading, selectedIds, handleSelect } = useCompanySearch({
    domainIds,
    enabled: isOpen, // Only load when sheet is opened
  })

  const { handleCompanySave, isSaving } = useCompanySave(
    companies,
    setCompanyLists,
  )

  const [filters, setFilters] = useState<CompanyFiltersType>({
    location: [],
    industries: [],
    companySizes: [],
    companyTypes: [],
    technologies: [],
  })

  const filteredCompanies = useMemo(() => {
    return filterCompanies(companies, filters)
  }, [companies, filters])

  const anyFiltersSet =
    filters.location.length > 0 ||
    filters.industries.length > 0 ||
    filters.companySizes.length > 0 ||
    filters.companyTypes.length > 0 ||
    filters.technologies.length > 0 ||
    filters.yearFounded !== undefined

  const handleSaveCompanies = async (_name: string, listId: number | null) => {
    const companiesToSave =
      selectedIds.length > 0
        ? filteredCompanies.filter((company) =>
            selectedIds.includes(company.id),
          )
        : filteredCompanies

    const domainIdsToSave = companiesToSave.map((company) => company.id)

    await handleCompanySave(domainIdsToSave, listId || undefined)
  }

  const saveButtonCount =
    selectedIds.length > 0 ? selectedIds.length : filteredCompanies.length

  const saveButtonText =
    saveButtonCount > 0
      ? `Save ${CurrencyHelper.numberWithDelimiter(saveButtonCount)} companies`
      : "Save companies"

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="default" size="xs">
          Show companies
        </Button>
      </SheetTrigger>
      <SheetContent size="lg" className="p-0">
        <SheetHeader>
          <SheetTitle>Discover new companies</SheetTitle>
        </SheetHeader>
        <CompanyFilters
          filters={filters}
          setFilters={setFilters}
          clearFilters={() => clearFilters(setFilters)}
          anyFiltersSet={anyFiltersSet}
        />
        <CompaniesList
          className="px-4"
          companies={filteredCompanies}
          selectedIds={selectedIds}
          onSelect={handleSelect}
          isLoading={isLoading}
          emptyMessage={
            anyFiltersSet
              ? "No companies match your filters"
              : "No companies found"
          }
          onClearFilters={
            anyFiltersSet ? () => clearFilters(setFilters) : undefined
          }
        />
        <SheetFooter sticky>
          <ButtonGroup>
            <Button
              variant="primary"
              disabled={isSaving || isLoading}
              onClick={() => handleSaveCompanies("", null)}
            >
              {saveButtonText}
            </Button>

            <CompanySaveManager
              id={id}
              companyLists={companyLists}
              setCompanyLists={setCompanyLists}
              onSave={handleSaveCompanies}
              disableButtons={isSaving || isLoading}
              count={
                selectedIds.length > 0
                  ? selectedIds.length
                  : filteredCompanies.length
              }
            >
              <Button
                variant="primary"
                disabled={isSaving || isLoading}
                iconOnly
              >
                <Icon icon="angle-down" size="sm" aria-hidden="true" />
              </Button>
            </CompanySaveManager>
          </ButtonGroup>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export { OpportunityCompaniesSidepanel }
